// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { store } from '@/store'
import { fetchWithTimeout } from '@/common/shared'
import { compareDesc } from 'date-fns'
import { ehrCrudUrl, pathdsRoute } from '@/settings'

export const getPathology = async (patientId) => {
  if (!patientId) { return [] }

  try {
    const fetchParams = {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
      },
    }

    const [collectionResult, reportsResult] = await Promise.all([
      getCollectionsData(patientId, fetchParams),
      getReportsData(patientId, fetchParams),
    ])

    // Process results
    const filteredReports = reportsResult.reports.filter(r => r.report_type?.short_name === 'PATHOLOGY')
    const mergedResult = [...collectionResult.items, ...filteredReports]

    const dateLookup = {}
    mergedResult.forEach((r) => {
      if (r.biopsy_time) { dateLookup[r.biopsy_time] = new Date(r.biopsy_time) }
      if (r.ordering_date) { dateLookup[r.ordering_date] = new Date(r.ordering_date) }
    })

    mergedResult.sort((a, b) => {
      const dateA = a.biopsy_time ? dateLookup[a.biopsy_time] : dateLookup[a.ordering_date]
      const dateB = b.biopsy_time ? dateLookup[b.biopsy_time] : dateLookup[b.ordering_date]

      return compareDesc(dateA, dateB)
    })

    const formattedResult = mergedResult.map(formatResult)
    return formattedResult
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
  }
  return []
}

const getCollectionsData = async (patientId, params) => {
  const collectionUrl = `${pathdsRoute}/api/v1/iiif/patient/${patientId}/biopsy/collection`
  try {
    const collectionResponse = await fetchWithTimeout(collectionUrl, params)
    if (collectionResponse.ok) {
      return await collectionResponse.json()
    } else {
      console.log(`Collection fetch error: status=${collectionResponse.status}`)
    }
  } catch (e) {
    console.error('Error fetching collection:', e)
  }
  return { items: [] }
}

const getReportsData = async (patientId, params) => {
  const reportsUrl = `${ehrCrudUrl}/report-store/patient-identifier/${patientId}`
  try {
    const reportsResponse = await fetchWithTimeout(reportsUrl, params)
    if (reportsResponse.ok) {
      return await reportsResponse.json()
    } else {
      console.log(`Reports fetch error: status=${reportsResponse.status}`)
    }
  } catch (e) {
    console.error('Error fetching reports:', e)
  }
  return { reports: [] }
}

const formatResult = (pathologyResult) => {
  const formatResult = {
    date: pathologyResult.biopsy_time || pathologyResult.ordering_date,
    specimenType: pathologyResult.specimen_type || pathologyResult?.identifiers?.find(i => i.identifier_type === 'specimen_type')?.identifier || null,
    accessionNumber: pathologyResult.accession_number || pathologyResult?.identifiers?.find(i => i.identifier_type === 'accession_num')?.identifier || null,
    labName: pathologyResult.lab_name || pathologyResult?.identifiers?.find(i => i.identifier_type === 'lab_name')?.identifier || null,
    testType: pathologyResult.test_type || null,
    thumbnail: pathologyResult.thumbnail || null,
    id: pathologyResult.id || pathologyResult.uuid || null,
    mimeType: pathologyResult.mimetype || null,
  }
  return formatResult
}
